/* Email a report to E3 people (a generic copy of the Project Status Report's dialog,
   PsrReportPage.js, for Invoice History's report; that one is left as it is). The PDF is built
   by the caller (buildPdf → { bytes, pages }), sent by the caller (send → the server route, which
   checks every address is @e3es.com and mails it from noreply@e3es.com, replies to the sender). */
import React, { useEffect, useRef, useState } from 'react';
import { getPMList } from '../../api';

const INK = '#1F1D1A';
const INK2 = '#4A463F';
const GREY = '#6B665E';
const RED = '#B3402E';
const SANS = "'Geist', system-ui, sans-serif";

const E3_ADDRESS = /^[^\s@]+@e3es\.com$/i;
const MAX_TO = 20;
const MailIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" />
  </svg>
);

const EmailReportDialog = ({ heading = 'Email this report', subheading, filename, pages, buildPdf, send: sendIt, onClose, onSent }) => {
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState([]); // [{ email, name }]
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [active, setActive] = useState(0);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null); // null | 'pdf' | 'send'
  const inputRef = useRef(null);
  const busy = !!stage;

  useEffect(() => {
    let alive = true;
    getPMList().then((rows) => { if (alive) setPeople((rows || []).filter((p) => E3_ADDRESS.test(p.email || ''))); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const q = text.trim().toLowerCase();
  const taken = (email) => to.some((t) => t.email.toLowerCase() === email.toLowerCase());
  const suggestions = q
    ? people.filter((p) => !taken(p.email) && ((p.name || '').toLowerCase().includes(q) || p.email.toLowerCase().includes(q))).slice(0, 6)
    : [];
  const add = (p) => {
    if (to.length >= MAX_TO) { setError(`Up to ${MAX_TO} people at a time.`); return; }
    setTo((t) => [...t, { email: p.email.toLowerCase(), name: p.name || '' }]);
    setText(''); setActive(0); setError(null);
  };
  // Enter / Tab / comma: the highlighted suggestion, else the typed address (E3 only).
  const commit = () => {
    const v = text.trim().replace(/[,;]+$/, '');
    if (!v) return false;
    if (suggestions[active]) { add(suggestions[active]); return true; }
    if (!E3_ADDRESS.test(v)) { setError(`${v} can’t receive this. E3 addresses only (@e3es.com).`); return true; }
    if (taken(v)) { setText(''); return true; }
    add(people.find((p) => p.email.toLowerCase() === v.toLowerCase()) || { email: v });
    return true;
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (commit()) e.preventDefault();
    } else if (e.key === 'Backspace' && !text && to.length) {
      setTo((t) => t.slice(0, -1));
    } else if (e.key === 'ArrowDown' && suggestions.length) {
      e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && suggestions.length) {
      e.preventDefault(); setActive((a) => Math.max(a - 1, 0));
    }
  };

  const send = async () => {
    if (busy) return;
    // Whatever is still typed counts (state updates land after this click, so build the list here).
    let list = to;
    const typed = text.trim().replace(/[,;]+$/, '');
    if (typed) {
      const pick = suggestions[active] || (E3_ADDRESS.test(typed) ? (people.find((p) => p.email.toLowerCase() === typed.toLowerCase()) || { email: typed }) : null);
      if (!pick) { setError(`${typed} can’t receive this. E3 addresses only (@e3es.com).`); return; }
      if (!taken(pick.email)) list = [...to, { email: pick.email.toLowerCase(), name: pick.name || '' }];
      setTo(list); setText('');
    }
    if (!list.length) { setError('Add at least one E3 person.'); return; }
    if (list.length > MAX_TO) { setError(`Up to ${MAX_TO} people at a time.`); return; }
    setError(null);
    try {
      setStage('pdf');
      const { toBase64 } = await import('../../lib/psrPdf');
      const { bytes, pages: pdfPages } = await buildPdf();
      setStage('send');
      const res = await sendIt({ to: list.map((t) => t.email), message: note.trim(), filename, pdf_base64: toBase64(bytes), pages: pdfPages });
      onSent((res && res.recipients) || list.length);
    } catch (e) {
      setError(e.message || 'The report couldn’t be sent. Try again.');
      setStage(null);
    }
  };

  const field = { width: '100%', boxSizing: 'border-box', border: '1px solid #D6D2CA', borderRadius: 8, background: '#fff', font: `400 14px ${SANS}`, color: INK };
  return (
    <div className="psr-dialog" role="dialog" aria-modal="true" aria-label="Email this report"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 5, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(31,29,26,.36)' }}>
      <div style={{ width: 520, maxWidth: '100%', background: '#fff', borderRadius: 14, border: '1px solid #E0DDD8', boxShadow: '0 24px 60px -12px rgba(0,0,0,.28)', padding: '18px 20px 16px', fontFamily: SANS, color: INK }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-.01em' }}>{heading}</div>
            {subheading && <div style={{ marginTop: 3, fontSize: 13.5, color: GREY }}>{subheading}</div>}
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" style={{ width: 26, height: 26, border: 'none', background: 'transparent', color: GREY, cursor: busy ? 'default' : 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        <label style={{ display: 'block', marginTop: 14, fontSize: 12.5, fontWeight: 600, color: INK2 }}>To</label>
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); inputRef.current && inputRef.current.focus(); } }}
          style={{ ...field, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 5, padding: '5px 8px', minHeight: 38, cursor: 'text' }}>
          {to.map((t) => (
            <span key={t.email} title={t.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 4px 0 9px', borderRadius: 999, background: '#EEF2EA', color: '#3F5A36', fontSize: 13, fontWeight: 500 }}>
              {t.name || t.email}
              <button type="button" aria-label={`Remove ${t.name || t.email}`} disabled={busy} onClick={() => setTo((all) => all.filter((x) => x.email !== t.email))}
                style={{ width: 18, height: 18, border: 'none', borderRadius: 999, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
          <input ref={inputRef} value={text} autoFocus disabled={busy}
            onChange={(e) => { setText(e.target.value); setActive(0); setError(null); }} onKeyDown={onKeyDown}
            placeholder={to.length ? '' : 'Name or name@e3es.com'} aria-label="Add a recipient"
            style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', font: `400 14px ${SANS}`, color: INK, background: 'transparent', padding: '3px 2px' }} />
        </div>
        {suggestions.length > 0 && (
          <div role="listbox" style={{ marginTop: 4, border: '1px solid #E0DDD8', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
            {suggestions.map((p, i) => (
              <div key={p.email} role="option" aria-selected={i === active} onMouseDown={(e) => { e.preventDefault(); add(p); }} onMouseEnter={() => setActive(i)}
                style={{ padding: '7px 10px', fontSize: 13.5, cursor: 'pointer', background: i === active ? '#F5F3EF' : '#fff' }}>
                {p.name} <span style={{ color: GREY }}>· {p.email}</span>
              </div>
            ))}
          </div>
        )}

        <label style={{ display: 'block', marginTop: 12, fontSize: 12.5, fontWeight: 600, color: INK2 }}>Message <span style={{ fontWeight: 400, color: GREY }}>(optional)</span></label>
        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} disabled={busy} rows={3}
          placeholder="What should they look at?" style={{ ...field, marginTop: 5, padding: '8px 10px', resize: 'vertical', lineHeight: 1.45 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', background: '#F5F3EF', borderRadius: 8, fontSize: 13.5 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5" /></svg>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</span>
          <span style={{ color: GREY, whiteSpace: 'nowrap' }}>{pages} page{pages === 1 ? '' : 's'}</span>
        </div>

        {error && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: RED, lineHeight: 1.4 }}>{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: GREY }}>From noreply@e3es.com. Replies come to you.</span>
          <button type="button" className="psr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="psr-btn primary" disabled={busy} onClick={send}>
            <MailIcon />{stage === 'pdf' ? 'Preparing PDF…' : stage === 'send' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailReportDialog;
